import type * as React from 'react'
import { createContext, useCallback, useContext, useMemo } from 'react'
import {
  type ScheduledInterview,
  toClientInterviewInput,
} from '@/lib/dashboard-interviews'
import { trpc } from '@/lib/trpc'

interface InterviewsContextValue {
  interviews: ScheduledInterview[]
  isCoachingClient: boolean
  isLoading: boolean
  isSaving: boolean
  error: Error | null
  mutationError: Error | null
  addInterview: (interview: ScheduledInterview) => Promise<void>
  updateInterview: (interview: ScheduledInterview) => Promise<void>
  removeInterview: (id: string) => Promise<void>
}

const InterviewsContext = createContext<InterviewsContextValue | null>(null)
export { InterviewsContext }

function toError(error: unknown): Error | null {
  if (!error) return null
  if (error instanceof Error) return error
  if (
    typeof error === 'object' &&
    'message' in error &&
    typeof error.message === 'string'
  ) {
    return new Error(error.message)
  }
  return null
}

export function InterviewsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const utils = trpc.useUtils()
  const query = trpc.dashboard.interviews.list.useQuery()

  const createMutation = trpc.dashboard.interviews.create.useMutation()
  const updateMutation = trpc.dashboard.interviews.update.useMutation()
  const removeMutation = trpc.dashboard.interviews.remove.useMutation()

  const invalidate = useCallback(
    () => utils.dashboard.interviews.list.invalidate(),
    [utils],
  )

  const resetMutationErrors = useCallback(() => {
    createMutation.reset()
    updateMutation.reset()
    removeMutation.reset()
  }, [createMutation, removeMutation, updateMutation])

  const addInterview = useCallback(
    async (interview: ScheduledInterview) => {
      resetMutationErrors()
      await createMutation.mutateAsync(toClientInterviewInput(interview))
      await invalidate()
    },
    [createMutation, invalidate, resetMutationErrors],
  )

  const updateInterview = useCallback(
    async (interview: ScheduledInterview) => {
      resetMutationErrors()
      await updateMutation.mutateAsync({
        id: interview.id,
        interview: toClientInterviewInput(interview),
      })
      await invalidate()
    },
    [invalidate, resetMutationErrors, updateMutation],
  )

  const removeInterview = useCallback(
    async (id: string) => {
      resetMutationErrors()
      await removeMutation.mutateAsync({ id })
      await invalidate()
    },
    [invalidate, removeMutation, resetMutationErrors],
  )

  const value = useMemo(
    () => ({
      interviews: query.data?.interviews ?? [],
      isCoachingClient: query.data?.isCoachingClient ?? false,
      isLoading: query.isLoading,
      isSaving:
        createMutation.isPending ||
        updateMutation.isPending ||
        removeMutation.isPending,
      error: toError(query.error),
      mutationError:
        [createMutation.error, updateMutation.error, removeMutation.error]
          .map(toError)
          .find((error) => error !== null) ?? null,
      addInterview,
      updateInterview,
      removeInterview,
    }),
    [
      query.data?.interviews,
      query.data?.isCoachingClient,
      query.isLoading,
      query.error,
      createMutation.error,
      createMutation.isPending,
      updateMutation.error,
      updateMutation.isPending,
      removeMutation.error,
      removeMutation.isPending,
      addInterview,
      updateInterview,
      removeInterview,
    ],
  )

  return (
    <InterviewsContext.Provider value={value}>
      {children}
    </InterviewsContext.Provider>
  )
}

export function useInterviews() {
  const ctx = useContext(InterviewsContext)
  if (!ctx) {
    throw new Error('useInterviews must be used within InterviewsProvider')
  }
  return ctx
}
